/**
 * Product Add-Ons, edited alongside their product (item 10, 2026-09-18 batch).
 *
 * Mirrors `ncbSlabs.ts` exactly on purpose: same shape of problem (a set of
 * child rows that only make sense once the parent product exists), same
 * "diff against what the server last said" approach so an untouched row is
 * never rewritten and a save never has to guess what changed.
 *
 * `code` is NOT auto-generated server-side the way it is for Product/Group/
 * Subgroup/PolicyType -- `ProductAddOn.code` is a plain required `CharField`
 * with no default and no serializer-level generation -- so, unlike those,
 * this form collects it.
 */

export interface AddOnDraft {
  id?: string;
  name: string;
  code: string;
  addon_type: string;
  is_percentage_based: boolean;
  percentage_of_premium: string;
  fixed_premium: string;
  is_mandatory: boolean;
}

export const ADDON_TYPE_OPTIONS = [
  { value: 'cover', label: 'Additional Cover' },
  { value: 'rider', label: 'Rider' },
  { value: 'benefit', label: 'Benefit' },
];

export const emptyAddOn = (): AddOnDraft => ({
  name: '',
  code: '',
  addon_type: 'cover',
  is_percentage_based: true,
  percentage_of_premium: '',
  fixed_premium: '',
  is_mandatory: false,
});

/** A row the user actually filled in -- name and code are the model's only
 * two required fields with no default. */
export const addOnFilled = (a: AddOnDraft) => a.name.trim() !== '' && a.code.trim() !== '';

export const addOnsEqual = (a: AddOnDraft, b: AddOnDraft) =>
  a.name === b.name &&
  a.code === b.code &&
  a.addon_type === b.addon_type &&
  a.is_percentage_based === b.is_percentage_based &&
  a.percentage_of_premium === b.percentage_of_premium &&
  a.fixed_premium === b.fixed_premium &&
  a.is_mandatory === b.is_mandatory;

export interface AddOnDiff {
  removed: AddOnDraft[];
  created: AddOnDraft[];
  updated: AddOnDraft[];
}

/** What to send so the server's add-ons match the screen. See `ncbSlabDiff`
 * for the full reasoning -- this is the same function against a different
 * shape of row. */
export function addOnDiff(current: AddOnDraft[], original: AddOnDraft[]): AddOnDiff {
  const kept = current.filter(addOnFilled);
  const keptIds = new Set(kept.map((a) => a.id).filter(Boolean) as string[]);

  return {
    removed: original.filter((a) => a.id && !keptIds.has(a.id)),
    created: kept.filter((a) => !a.id),
    updated: kept.filter((a) => {
      if (!a.id) return false;
      const before = original.find((o) => o.id === a.id);
      return !!before && !addOnsEqual(before, a);
    }),
  };
}
